import type { UrlStatusUpdate } from '@kanbu/schema/contracts';
import { DocumentStatus } from '@kanbu/schema/enums';

type Props = {
  item: UrlStatusUpdate;
  isLoading?: boolean;
};

function cleanUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);

    return `${parsedUrl.hostname}${parsedUrl.pathname === '/' ? '' : parsedUrl.pathname}${parsedUrl.search}`.replace(
      /^www\./,
      '',
    );
  } catch {
    return url;
  }
}

export function UrlListItem({ item, isLoading }: Props) {
  let progress = 0;
  let isDone = false;
  let isError = false;

  switch (item.status) {
    case DocumentStatus.Uploading:
      progress = 16;
      break;

    case DocumentStatus.Ready:
      progress = 32;
      break;

    case DocumentStatus.Parsing:
    case DocumentStatus.ParsingAsync:
      progress = 48;
      break;

    case DocumentStatus.Parsed:
      progress = 64;
      break;

    case DocumentStatus.Embedding:
      progress = 80;
      break;

    case DocumentStatus.Done:
      progress = 100;
      isDone = true;
      break;

    case DocumentStatus.ParsingError:
    case DocumentStatus.EmbeddingError:
      progress = 100;
      isError = true;
      break;

    default:
      progress = 0;
      break;
  }

  const isInProgress = isLoading && !isDone && !isError;
  const cleanedUrl = cleanUrl(item.url);

  return (
    <li className='relative animate-[slide-in_0.3s_ease-out] overflow-hidden rounded-full'>
      {isInProgress && (
        <div
          className='absolute left-0 top-0 h-full overflow-hidden transition-all duration-500 ease-out'
          style={{ width: `${progress}%` }}
        >
          {/* Progress fill */}
          <div className='absolute inset-0 bg-gradient-to-r from-[#9c70ff]/30 to-[#b899ff]/30' />

          {/* Shimmer effect - constrained to progress width */}
          <div className='absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-[#9c70ff]/40 to-transparent' />
        </div>
      )}

      <div
        className={`relative flex items-center gap-2 rounded-full border px-3 py-1.5 transition-all duration-200 ${
          isDone
            ? 'border-[#22c55e]/30 bg-[#f0fdf4]'
            : isError
              ? 'border-[#ef4444]/30 bg-[#fef2f2]'
              : 'border-[#e5e5e5] bg-white/50'
        }`}
      >
        <span
          className={`truncate text-[11px] font-medium leading-tight transition-colors duration-200 ${
            isDone
              ? 'text-[#16a34a]'
              : isError
                ? 'text-[#ef4444]'
                : 'text-[#1a1a1a]'
          }`}
          title={item.url}
        >
          {cleanedUrl}
        </span>

        {isDone && (
          <div className='flex size-3.5 shrink-0 animate-[scale-in_0.3s_ease-out] items-center justify-center rounded-full bg-[#22c55e]'>
            <svg
              className='size-2 text-white'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={3}
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
        )}

        {isError && (
          <div className='flex size-3.5 shrink-0 animate-[scale-in_0.3s_ease-out] items-center justify-center rounded-full bg-[#ef4444]'>
            <svg
              className='size-2 text-white'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={3}
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </div>
        )}

        {isInProgress && (
          <div className='flex size-3.5 shrink-0 items-center justify-center'>
            <div className='size-2 animate-spin rounded-full border border-[#9c70ff] border-t-transparent' />
          </div>
        )}
      </div>
    </li>
  );
}
